// ユーザー設定のローカル⇄DB 同期の純粋ロジック。
// ローカル (localStorage / Tauri store) はキャッシュ、DB (user_settings) が同期の要。
// key 単位の last-write-wins で解決する。

/** ローカルに保存する封筒形式 (v2)。旧形式は設定オブジェクトそのままの JSON。 */
export interface StoredSettingEnvelope<T> {
  __v: 2;
  value: T;
  /** epoch ms。旧形式からの移行時は 0 (リモートがあればリモート優先にする) */
  updatedAt: number;
  /** DB への push が未完了 (オフライン等) */
  dirty: boolean;
}

export interface RemoteSetting<T> {
  value: T;
  updatedAt: number; // epoch ms
}

export type SyncAction = "adoptRemote" | "pushLocal" | "noop";

/**
 * ローカル保存文字列を封筒形式にパースする。
 * 旧形式 (設定オブジェクト直書き) は updatedAt=0, dirty=true として扱う:
 * - リモートに設定があれば必ずリモート優先 (remote.updatedAt > 0)
 * - リモートが無ければローカル値を初回 push する
 */
export function parseStoredSettings<T>(
  raw: string | null
): StoredSettingEnvelope<T> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return null;
    if (parsed.__v === 2 && typeof parsed.updatedAt === "number") {
      return {
        __v: 2,
        value: parsed.value as T,
        updatedAt: parsed.updatedAt,
        dirty: parsed.dirty === true,
      };
    }
    // 旧形式
    return { __v: 2, value: parsed as T, updatedAt: 0, dirty: true };
  } catch {
    return null;
  }
}

export function serializeStoredSettings<T>(
  envelope: StoredSettingEnvelope<T>
): string {
  return JSON.stringify(envelope);
}

/**
 * ローカルとリモートのどちらを採用するか決める。
 * - リモートの方が新しい → adoptRemote (dirty なローカル編集も LWW で敗北)
 * - ローカルが dirty、またはリモート行がまだ無い → pushLocal
 * - それ以外 (同期済み) → noop
 */
export function resolveSettingSync<T>(
  local: StoredSettingEnvelope<T> | null,
  remote: RemoteSetting<T> | null
): SyncAction {
  if (!local) {
    return remote ? "adoptRemote" : "noop";
  }
  if (remote && remote.updatedAt > local.updatedAt) {
    return "adoptRemote";
  }
  if (local.dirty || !remote) {
    return "pushLocal";
  }
  return "noop";
}
